import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import axios from 'axios';

interface Member {
  id: number;
  name: string;
  member_no: string;
  id_no: string;
  email: string;
  date_registered: string;
  group_id: number;
  status: string;
  phone_number: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  additional_data?: any;
}

interface MembersResponse {
  data: Member[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

interface MemberStats {
  total: number;
  active: number;
  inactive: number;
  admins: number;
}

interface MembersFilters {
  search?: string;
  status?: 'Active' | 'Inactive' | 'all';
  sort_field?: string;
  sort_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

// Interface for members returned by loan application endpoint
interface LoanApplicationMember {
  id: number;
  name: string;
  phone_number: string;
}

export const useMembers = (filters: MembersFilters = {}) => {
  return useQuery<MembersResponse>({
    queryKey: ['members', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      
      const { data } = await api.get(`/members?${params.toString()}`);
      return data;
    },
  });
};

// Hook for fetching members for loan application (guarantor selection)
export const useMembersForLoanApplication = (options = {}) => {
  return useQuery<{ success: boolean; data: LoanApplicationMember[] }>({
    queryKey: ['members-loan-application'],
    queryFn: async () => {
      const { data } = await api.get('/members/loan-application');
      return data;
    },
    ...options,
  });
};

export const useMemberStats = () => {
  return useQuery<{ data: MemberStats }>({
    queryKey: ['memberStats'],
    queryFn: async () => {
      const { data } = await api.get('/members/statistics');
      return data;
    },
  });
};

export const useCreateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newMember: Omit<Member, 'id' | 'created_at' | 'updated_at'>) => {
      const { data } = await api.post('/members', newMember);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['memberStats'] });
    },
  });
};

export const useUpdateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...member }: Partial<Member> & { id: number }) => {
      const { data } = await api.put(`/members/${id}`, member);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['memberStats'] });
    },
  });
};

export const useDeleteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: number | { id: number; reassign_admin_to?: number }) => {
      const id = typeof params === 'number' ? params : params.id;
      const reassignAdminTo = typeof params === 'number' ? undefined : params.reassign_admin_to;
      const { data } = await api.delete(`/members/${id}`, {
        data: reassignAdminTo ? { reassign_admin_to: reassignAdminTo } : undefined,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['memberStats'] });
    },
  });
};

export const useExportMembers = () => {
  return useMutation({
    mutationFn: async (filters: Omit<MembersFilters, 'page' | 'per_page'>) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      
      const response = await api.get(`/members/export-data`, {
        responseType: 'blob',
      });
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'members.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
}; 